import { useMemo, useState, type ReactNode } from 'react';
import { useLevelEntities } from '../../../../hooks/useLevelEntities';
import { ParentSelect, sortByName } from '../../../../components/scoped/LevelEntityPicker';
import type { LevelNameLocal } from '../../../../api/types/LevelEntityLocal';

interface Props {
    tenantId: string;
    /** Nivel con padre que se gestiona (LEVEL_2 o LEVEL_3). */
    level: Exclude<LevelNameLocal, 'LEVEL_1'>;
    /** Nombre legible de cada nivel del tenant. */
    nameOf: (level: LevelNameLocal) => string;
    /** Contenido del nivel una vez elegido el padre inmediato. */
    children: (parentId: string) => ReactNode;
}

/** Cadena de padres de un nivel: se elige el padre y se muestran sus registros. */
export function LevelParentScope({ tenantId, level, nameOf, children }: Props) {
    const [parentL1, setParentL1] = useState('');
    const [parentL2, setParentL2] = useState('');
    const needL2 = level === 'LEVEL_3';

    const l1 = useLevelEntities(tenantId, 'LEVEL_1');
    const l2 = useLevelEntities(needL2 && parentL1 ? tenantId : undefined, 'LEVEL_2', parentL1);
    const l1Items = useMemo(() => sortByName(l1.data ?? []), [l1.data]);
    const l2Items = useMemo(() => sortByName(l2.data ?? []), [l2.data]);

    const parentId = needL2 ? parentL2 : parentL1;
    const parentLevel: LevelNameLocal = needL2 ? 'LEVEL_2' : 'LEVEL_1';
    const noRoots = !l1.isLoading && !l1.isError && l1Items.length === 0;

    if (noRoots) {
        return (
            <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
                {`Todavía no hay registros de ${nameOf('LEVEL_1').toLowerCase()}. Créalos primero en su pestaña.`}
            </p>
        );
    }

    return (
        <div className="space-y-4">
            <div className="grid max-w-xl gap-3 sm:grid-cols-2">
                <ParentSelect
                    id="lps-parent-l1"
                    label={nameOf('LEVEL_1')}
                    placeholder={`Elige ${nameOf('LEVEL_1')}`}
                    items={l1Items}
                    value={parentL1}
                    onChange={(id) => {
                        setParentL1(id);
                        setParentL2('');
                    }}
                    error={l1.isError}
                />
                {needL2 && (
                    <ParentSelect
                        id="lps-parent-l2"
                        label={nameOf('LEVEL_2')}
                        placeholder={`Elige ${nameOf('LEVEL_2')}`}
                        items={l2Items}
                        value={parentL2}
                        onChange={setParentL2}
                        disabled={!parentL1}
                        error={l2.isError}
                    />
                )}
            </div>
            {parentId ? (
                <div key={parentId}>{children(parentId)}</div>
            ) : (
                <p className="text-sm text-muted-foreground">
                    {`Elige ${nameOf(parentLevel)} para ver y crear registros de ${nameOf(level).toLowerCase()}.`}
                </p>
            )}
        </div>
    );
}
